import React, { useEffect, useRef, useState } from "react";
import { cn } from "@penmate/react-shared/lib/utils";
import { US_STATES } from "@penmate/react-shared/lib/usStates";
import type { Facility } from "@penmate/react-shared/api/facilities";
import {
  createSearch,
  updateSearch,
  updateSearchKnownFacility,
  type Candidate,
  type CreateSearchBody,
} from "../api/searches";
import { addPenmate, type Penmate } from "../api/penmates";
import { findFacilityById } from "../api/facilities";
import type { PmReactSearchProps, SearchEvent } from "../PmReactSearchApp";
import { WhoStep } from "./WhoStep";
import { WhereStep } from "./WhereStep";
import { WaitStep, newWaitMemory } from "./WaitStep";
import { HandoffStep } from "./HandoffStep";
import type { FlowStep, SearchDraft, SearchPrefill } from "./types";
import heroIllustration from "../assets/hero-illus-main.png";
import { PlaneOrbit } from "./PlaneOrbit";
import { useKeyboard } from "./useKeyboard";
import { KeyboardLayoutProvider, isTextField, seatField, unseat } from "./keyboardLayout";
import { callName } from "./names";

// The search↔contact association, recorded at add time so the my-penmates card
// can carry passive search state later (the follow-up slice reads this).
export const SEARCH_ASSOC_KEY = "pmreact.search.assoc.v1";

// `matched` says whether the user confirmed a candidate from the search: only
// a confirmed match can earn the contact card's "Location verified" badge.
const recordAssociation = (searchId: number | null, penmate: Penmate | null, matched: boolean): void => {
  if (searchId == null || !penmate) return;
  try {
    window.localStorage.setItem(
      SEARCH_ASSOC_KEY,
      JSON.stringify({ searchId, penmateId: penmate.id, matched, at: Date.now() }),
    );
  } catch {
    // ignore
  }
};

const searchBody = (draft: SearchDraft): CreateSearchBody => {
  const body: CreateSearchBody = {
    first_name: draft.firstName,
    last_name: draft.lastName,
    state: draft.mode === "federal" ? "FEDERAL" : draft.state || "",
  };
  if (draft.inmateId) body.inmate_id = draft.inmateId;
  if (draft.birthdate) body.birthdate = draft.birthdate;
  return body;
};

// Discrete step position for the segment indicator. Federal skips "where"
// (no facility typeahead — federal is nationwide via BOP).
const stepPosition = (
  step: FlowStep,
  draft: SearchDraft | null,
): { position: number; total: number } => {
  const total = draft && draft.mode === "federal" ? 2 : 3;
  const position = step === "who" ? 1 : step === "where" ? 2 : total;
  return { position, total };
};

const STEP_ORDER: FlowStep[] = ["who", "where", "wait", "done"];

// The indicator doubles as the "what happens" framing: labeled segments teach
// the sequence without a step list.
const stepLabels = (total: number): string[] =>
  total === 2 ? ["Who", "Search"] : ["Who", "Where", "Search"];

const sameDraft = (a: SearchDraft, b: SearchDraft): boolean =>
  a.mode === b.mode &&
  a.firstName === b.firstName &&
  a.lastName === b.lastName &&
  (a.state || "") === (b.state || "") &&
  (a.inmateId || "") === (b.inmateId || "") &&
  (a.birthdate || "") === (b.birthdate || "");

// A prefill seeds the first screen as an uncommitted state-mode draft.
const prefillInitial = (prefill: SearchPrefill | undefined): Partial<SearchDraft> | undefined => {
  if (!prefill) return undefined;
  const initial: Partial<SearchDraft> = { mode: "state" };
  if (prefill.firstName) initial.firstName = prefill.firstName;
  if (prefill.lastName) initial.lastName = prefill.lastName;
  // Only a real state seeds the select; anything else would enable the CTA
  // against a blank control and fire a search with a bogus state.
  const state = prefill.state ? prefill.state.toUpperCase() : "";
  if (state && US_STATES.some((s) => s.abbrev === state)) initial.state = state;
  return initial;
};

const sameState = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && a.toUpperCase() === b.toUpperCase();

// pending/failed are per action so a keep-adding failure can't paint the match
// card's confirm button, and a pending keep-add can freeze the wait surface
// (a late match must not swap the UI under an in-flight add).
type AddState = {
  pending: null | "confirm" | "keep";
  failed: null | "confirm" | "keep";
};

export const SearchFlow = (props: PmReactSearchProps) => {
  const { isAuthenticated, resumeQuery, prefill, onNavigate, onEvent, getMetricsContext, registerLeave } =
    props;

  // Analytics must never break the flow — a throwing host callback is ignored.
  const emit = (event: SearchEvent) => {
    if (!onEvent) return;
    try {
      onEvent(event);
    } catch {
      // ignore
    }
  };
  const emitRef = useRef(emit);
  emitRef.current = emit;

  const [draft, setDraft] = useState<SearchDraft | null>(resumeQuery || null);
  const [step, setStep] = useState<FlowStep>(() => {
    if (resumeQuery) return resumeQuery.mode === "federal" ? "wait" : "where";
    return "who";
  });
  const [knownFacility, setKnownFacility] = useState<Facility | null>(null);
  // What was typed into the facility field: an alias or a misspelling the
  // typeahead couldn't place. It survives Back/forward like the pick and, when
  // no pick came of it, rides the add as a hint for whoever reviews the contact.
  const [facilityQuery, setFacilityQuery] = useState("");
  // Whether the typeahead found nothing for that text: only a true miss may
  // stand as the contact's free-text facility; a prefix with results showing
  // is a hint for review, nothing more.
  const [facilityMiss, setFacilityMiss] = useState(false);
  // The settled wait's optional birthday, owned here like the facility pick:
  // it belongs to the person, so a retry's fresh wait or a Back excursion
  // must not destroy what the user typed.
  const [lateBirthdate, setLateBirthdate] = useState("");
  const [heroRest, setHeroRest] = useState(false);
  // A new search (re-fire or retry) gets a fresh wait: its own clocks, its own
  // memory of what was shown and declined.
  const [waitGen, setWaitGen] = useState(0);
  const waitMemory = useRef(newWaitMemory());
  const freshWait = () => {
    waitMemory.current = newWaitMemory();
    setWaitGen((g) => g + 1);
  };
  // A facility arriving by id (facility-page links) pre-answers the where step.
  // The id rides the committed draft through login, so it resolves once auth
  // is known; it follows the active id and clears when the id goes away, so a
  // facility from one arrival can't pre-answer a later, unrelated flow on this
  // cached page instance.
  const [prefillFacility, setPrefillFacility] = useState<Facility | null>(null);
  const activeFacilityId = (draft && draft.facilityId) || (prefill && prefill.facilityId) || null;
  useEffect(() => {
    if (!activeFacilityId) {
      setPrefillFacility(null);
      return;
    }
    if (prefillFacility && String(prefillFacility.id) !== String(activeFacilityId)) {
      // A different id is active now; the old facility must not linger while
      // the new lookup is pending.
      setPrefillFacility(null);
    }
    if (!isAuthenticated) return;
    if (prefillFacility && String(prefillFacility.id) === String(activeFacilityId)) return;
    let alive = true;
    findFacilityById(activeFacilityId).then((facility) => {
      if (alive) setPrefillFacility(facility);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFacilityId, isAuthenticated]);
  // Only the facility for the active id counts, and only for the state it
  // belongs to: a changed State on the first screen must not carry a
  // pre-selection into the wrong search.
  const currentFacility =
    prefillFacility && activeFacilityId && String(prefillFacility.id) === String(activeFacilityId)
      ? prefillFacility
      : null;
  const facilityForDraft =
    currentFacility && draft && sameState(currentFacility.state, draft.state) ? currentFacility : null;
  const [searchId, setSearchId] = useState<number | null>(null);
  const [searchFailed, setSearchFailed] = useState(false);
  const [add, setAdd] = useState<AddState>({ pending: null, failed: null });
  const [added, setAdded] = useState<Penmate | null>(null);
  const hasAddedRef = useRef(false);
  const stepRef = useRef<FlowStep>(step);
  stepRef.current = step;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;

  // Every step is a history entry, so the device/browser back button means
  // "previous step with the form intact" — the same thing the in-flow Back
  // does. Each entry carries this flow instance's tag and its own position;
  // nothing is inferred from a counter, so entries pushed by other pages
  // (footer round trips, the login detour) can never throw it off.
  const flowId = useRef(`pm-search-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`);
  const entryState = () => {
    const st = typeof window !== "undefined" ? window.history.state : null;
    return st && st.pmSearch === flowId.current ? st : null;
  };
  const tagEntry = (next: FlowStep, push: boolean, pmDepth: number, announce = true): boolean => {
    try {
      const state = { ...(window.history.state || {}), pmSearch: flowId.current, step: next, pmDepth };
      if (push) window.history.pushState(state, "", window.location.href);
      else window.history.replaceState(state, "", window.location.href);
      // pushState fires no event, so the host page can't see the depth change
      // on its own; it re-reports back-button capability to the native shell
      // on this signal. An internal re-base stays silent — the push that
      // follows it announces the settled state.
      if (announce) {
        try {
          window.dispatchEvent(new CustomEvent("pm-history-state-changed"));
        } catch {
          // ignore
        }
      }
      return true;
    } catch {
      // History is a convenience; the flow works without it.
      return false;
    }
  };
  const advanceTo = (next: FlowStep) => {
    let current = entryState();
    if (!current) {
      // The current entry isn't ours (first advance, or another page pushed
      // since): make it the base for what follows.
      tagEntry(stepRef.current, false, 0, false);
      current = entryState();
    }
    tagEntry(next, true, current ? current.pmDepth + 1 : 1);
    setStep(next);
  };
  // Leaving the wait abandons any add still in flight: the contact may still
  // be created, but its completion must not land the handoff on a flow that
  // has moved on (possibly to a different person).
  const addGen = useRef(0);
  const abandonAdd = () => {
    addGen.current += 1;
    setAdd({ pending: null, failed: null });
  };
  const goBack = () => {
    abandonAdd();
    const current = entryState();
    if (current && current.pmDepth > 0) {
      window.history.back();
    } else {
      setStep("who");
      tagEntry("who", false, 0);
    }
  };
  useEffect(() => {
    tagEntry(stepRef.current, false, 0);
    const onPop = (e: PopStateEvent) => {
      const st = e.state;
      if (!st || st.pmSearch !== flowId.current || !st.step) return;
      if (st.step === stepRef.current) return;
      // Once the contact is added, the decision is spent: going back past the
      // handoff leaves the flow instead of reopening the match.
      if (hasAddedRef.current) {
        if (onNavigateRef.current) onNavigateRef.current({ to: "my-penmates" });
        return;
      }
      abandonAdd();
      setStep(st.step);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fired = useRef(false);
  // Leaving the page with a search underway and no add is the abandonment
  // signal the funnel otherwise lacks. Before a search fires, an open without
  // a start already tells the story.
  const searchIdRef = useRef(searchId);
  searchIdRef.current = searchId;
  const addPendingRef = useRef(add.pending);
  addPendingRef.current = add.pending;
  useEffect(() => {
    if (!registerLeave) return;
    registerLeave(() => {
      if (hasAddedRef.current || !fired.current) return;
      const step = stepRef.current;
      if (step === "done") return;
      emitRef.current({
        type: "search-flow-left",
        step,
        search_id: searchIdRef.current,
        wait_resolved: waitMemory.current.resolved,
        // An add still in flight at leave time usually lands; the funnel can
        // filter these rather than count them as abandonment.
        add_pending: addPendingRef.current !== null,
      });
    });
    return () => registerLeave(null);
  }, [registerLeave]);
  // A re-fire (back + changed draft) must not let the abandoned create's late
  // response win: each create carries a generation, and stale ones are ignored.
  const createGen = useRef(0);
  // Retained so an add can await a slow create: firing the add without the
  // search id would permanently orphan the search (there is no post-flow
  // attach by design).
  const createdSearchId = useRef<Promise<number | null> | null>(null);

  // The arrival facility rides the create as-is: the server treats an
  // unknown or stale id as best-effort (ignored, never a failure) and guards
  // the state match itself, so the send must not wait on this client's own
  // lookup — a resume fire after login would race it and drop the id for the
  // very arrivals it is for. Only a plainly-numeric spelling is sent at all:
  // the endpoint coerces Integer, so anything that would serialize as text
  // or a float (letters, fractions, ids so large JSON goes exponential)
  // must stay off the create rather than fail it, and Number() must never
  // get the chance to rewrite a garbage spelling into some other real id.
  const asKnownFacilityId = (value: unknown): number | null => {
    if (value == null) return null;
    const raw = String(value).trim();
    if (!/^\d{1,15}$/.test(raw)) return null;
    const id = Number(raw);
    return id > 0 ? id : null;
  };
  const knownFacilityIdFor = (committed: SearchDraft): number | null => {
    if (committed.mode !== "state" || !committed.facilityId) return null;
    return asKnownFacilityId(committed.facilityId);
  };

  // The known facility is RECONCILED onto the search (best-effort, like the
  // create's own known_facility_id): the resolver may fall back to a
  // facility the user named, and must stop the moment they walk it back —
  // "Help me find them" after a pick, or after the arrival facility's
  // confirmation, retracts it (null clears the pick server-side and
  // un-points resolutions it produced; a source-named facility stays).
  // Reconciling from state rather than firing at the pick means a pick made
  // during a slow create lands when the id does, a retry's fresh search
  // re-learns the preserved pick, and a re-committed draft (which clears
  // the pick in the same update) never inherits it. `sentKnownFacility` is
  // what this client has told the server for the current search — seeded by
  // the create's own send — so a search that never knew a facility is never
  // patched at all.
  const sentKnownFacility = useRef<{ sid: number; knownId: number | null } | null>(null);
  // The arrival facility never enters `knownFacility` (it pre-answers the
  // where step from `facilityForDraft`), so a bare null pick is not a
  // retraction until the user has actually answered the step — otherwise the
  // create-sent arrival id would be retracted the moment the create lands,
  // while "Is {first} at {facility}?" is still on screen. State, not a ref:
  // answering with "Help me find them" changes nothing else the effect
  // watches, yet must trigger the retraction.
  const [whereAnswered, setWhereAnswered] = useState(false);
  // Sends are chained so a quick pick → decline can't land out of order.
  const patchTail = useRef<Promise<unknown>>(Promise.resolve());
  const patchedBirthdate = useRef<{ sid: number; value: string } | null>(null);
  useEffect(() => {
    if (searchId == null) return;
    const knownId = asKnownFacilityId(knownFacility && knownFacility.id);
    const prev = sentKnownFacility.current;
    const serverHas = prev && prev.sid === searchId ? prev.knownId : null;
    if (knownId === serverHas) return;
    if (knownId == null && !whereAnswered) return;
    sentKnownFacility.current = { sid: searchId, knownId };
    const sid = searchId;
    patchTail.current = patchTail.current.then(() =>
      updateSearchKnownFacility(sid, knownId).catch(() => {
        // Best-effort: the pick still rides the add.
      }),
    );
  }, [searchId, knownFacility, whereAnswered]);

  // The commitment: fire the search exactly once per flow. Search is
  // enrichment — a failed fire never blocks the flow, it just means the wait
  // surface will have nothing to show.
  const fireSearch = (committed: SearchDraft) => {
    if (fired.current) return;
    fired.current = true;
    const gen = ++createGen.current;
    // The start event is emitted on settle so it carries the search id (null
    // when the create failed — the user still started a search).
    const started = (searchId2: number | null) =>
      emitRef.current({
        type: "search-started",
        search_id: searchId2,
        state: committed.mode === "federal" ? "FEDERAL" : committed.state || "",
        has_optional_id: !!committed.inmateId,
        federal: committed.mode === "federal",
      });
    const body = searchBody(committed);
    const knownFacilityId = knownFacilityIdFor(committed);
    if (knownFacilityId != null) body.known_facility_id = knownFacilityId;
    createdSearchId.current = createSearch(body)
      .then((search) => {
        if (gen !== createGen.current) return null;
        // The create already told the server this much; the reconciliation
        // effect diffs against it (and can retract it).
        sentKnownFacility.current = {
          sid: search.id,
          knownId: knownFacilityId != null ? knownFacilityId : null,
        };
        setSearchId(search.id);
        started(search.id);
        return search.id;
      })
      .catch(() => {
        if (gen !== createGen.current) return null;
        setSearchFailed(true);
        started(null);
        return null;
      });
  };

  // Top-of-funnel marker: one per flow instance. A tree born with a resumed
  // draft is the continuation of an open already counted before login.
  useEffect(() => {
    if (!resumeQuery) emitRef.current({ type: "search-opened" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A resumed draft (carried through login) is already committed. Both the
  // draft and auth can arrive on a later update() rather than at mount: the
  // host resolves auth non-blockingly, and Ionic reuses a cached view on the
  // post-login return, delivering the routed draft to this existing tree. So:
  // adopt the draft into flow state if the user hasn't committed one here, and
  // fire once auth is confirmed.
  useEffect(() => {
    if (!resumeQuery) return;
    if (!draft) {
      const next: FlowStep = resumeQuery.mode === "federal" ? "wait" : "where";
      setDraft(resumeQuery);
      setStep(next);
      // The current entry must describe the adopted step, or a later advance
      // + device back would skip straight past it.
      const current = entryState();
      tagEntry(next, false, current ? current.pmDepth : 0);
    }
    if (isAuthenticated) fireSearch(resumeQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeQuery, isAuthenticated, draft]);

  // The add: the one call that must not silently fail — failures surface with
  // retry. On success the flow lands on the handoff. Awaits a still-in-flight
  // create first so the search id is never dropped from the link.
  const performAdd = async (candidate: Candidate | null, fromSource?: string) => {
    if (!draft || add.pending) return;
    // A confirmed match needs no verification help; the typed birthday rides
    // only the keep-adding path.
    const birthday = candidate ? undefined : lateBirthdate || undefined;
    const action = candidate ? ("confirm" as const) : ("keep" as const);
    setAdd({ pending: action, failed: null });
    const gen = ++addGen.current;
    let sid = searchId;
    if (sid == null && createdSearchId.current) {
      sid = await createdSearchId.current;
    }
    // A late birthday also reaches the search row so any future assisted
    // lookup carries it. The facility key rides along re-asserted: the
    // endpoint reads an absent key as a retraction, and this must never
    // retract a pick as a side effect — so when the ref doesn't cover this
    // search, skip the PATCH entirely (the birthday still rides the add).
    // One send per (search, birthday): an add retry must not repeat it.
    const prev = sentKnownFacility.current;
    if (
      birthday &&
      sid != null &&
      prev &&
      prev.sid === sid &&
      !(patchedBirthdate.current && patchedBirthdate.current.sid === sid && patchedBirthdate.current.value === birthday)
    ) {
      patchedBirthdate.current = { sid, value: birthday };
      const patchSid = sid;
      const facilityAsSent = prev.knownId;
      patchTail.current = patchTail.current.then(() =>
        updateSearch(patchSid, {
          known_facility_id: facilityAsSent,
          birthdate: birthday,
        }).catch(() => {
          // Best-effort: the birthday still rides the add.
        }),
      );
    }
    let metricsContext: Record<string, unknown> | undefined;
    try {
      metricsContext = getMetricsContext ? getMetricsContext() : undefined;
    } catch {
      metricsContext = undefined;
    }
    // Typed text that never became a pick is the hint; a pick means it matched.
    const facilityHint = knownFacility ? "" : facilityQuery.trim();
    addPenmate({
      draft,
      candidate,
      knownFacility,
      facilityHint,
      facilityHintUnmatched: facilityMiss,
      searchId: sid,
      metricsContext,
      lateBirthdate: birthday,
      searchMeta: {
        matched: !!candidate,
        facility_hint_present: facilityHint.length > 0,
        from_source: fromSource || null,
        confirmed_facility:
          candidate && candidate.facility
            ? candidate.facility
            : knownFacility
              ? knownFacility.name
              : null,
        late_birthdate: !!birthday,
        // The ask renders exactly when the first screen collected nothing —
        // the denominator for the disclosure's fill rate.
        late_birthdate_offered: !candidate && !draft.birthdate,
      },
    })
      .then((penmate) => {
        recordAssociation(sid, penmate, !!candidate);
        emitRef.current({
          type: "search-add-succeeded",
          penmate_id: penmate ? penmate.id : null,
        });
        // The contact exists even when this flow moved on, so the decision is
        // spent either way: back exits, and a later leave is not abandonment.
        hasAddedRef.current = true;
        if (gen !== addGen.current) return;
        setAdded(penmate);
        setAdd({ pending: null, failed: null });
        advanceTo("done");
      })
      .catch(() => {
        if (gen !== addGen.current) return;
        setAdd({ pending: null, failed: action });
      });
  };

  // The host mounts before its auth probe settles (isAuthenticated undefined).
  // A pre-filled screen is commit-ready immediately, so a tap in that window
  // waits for the answer instead of bouncing a signed-in user to login.
  const [pendingCommit, setPendingCommit] = useState<SearchDraft | null>(null);
  const authPending = isAuthenticated === undefined;
  useEffect(() => {
    if (authPending || !pendingCommit) return;
    const committed = pendingCommit;
    setPendingCommit(null);
    commitWho(committed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPending, pendingCommit]);

  const commitWho = (committed: SearchDraft) => {
    if (authPending) {
      setPendingCommit(committed);
      return;
    }
    if (!isAuthenticated) {
      // The flow starts logged-out; auth happens at this commitment point. The
      // draft carries through login and comes back as resumeQuery (with an
      // inbound facility id, if any, so the where step can still start from it).
      const query: SearchDraft =
        committed.mode === "state" && activeFacilityId
          ? { ...committed, facilityId: activeFacilityId }
          : committed;
      if (onNavigate) onNavigate({ to: "login", query });
      return;
    }
    // Coming back and changing who we're looking for means a new search; an
    // unchanged draft keeps the one already running (unless its create failed
    // — then a re-commit is the retry).
    if (draft && (!sameDraft(draft, committed) || searchFailed)) {
      fired.current = false;
      createdSearchId.current = null;
      setSearchId(null);
      setSearchFailed(false);
      setKnownFacility(null);
      setFacilityQuery("");
      setFacilityMiss(false);
      setLateBirthdate("");
      setWhereAnswered(false);
      abandonAdd();
      freshWait();
    }
    // Carry the inbound facility id on the committed draft (state mode only) so
    // it survives the login round trip and a later re-commit.
    const withFacility: SearchDraft =
      committed.mode === "state" && activeFacilityId
        ? { ...committed, facilityId: activeFacilityId }
        : committed;
    setDraft(withFacility);
    fireSearch(withFacility);
    advanceTo(withFacility.mode === "federal" ? "wait" : "where");
  };

  const backToWho = () => goBack();

  // A failed create can be retried in place: same draft, new attempt.
  const retrySearch = () => {
    if (!draft) return;
    fired.current = false;
    createdSearchId.current = null;
    setSearchFailed(false);
    freshWait();
    fireSearch(draft);
  };

  const { position, total } = stepPosition(step, draft);

  // While someone types, the keyboard owns the bottom of the screen: the hero
  // and step labels give way (styles keyed off `data-keyboard`), the sheet
  // pads past the keyboard, and the focused field is seated clear of it.
  const keyboard = useKeyboard();
  const scroller = useRef<HTMLDivElement | null>(null);
  const focusedField = useRef<HTMLElement | null>(null);
  const [typing, setTyping] = useState(false);
  const rearranging = keyboard.open && typing;
  // On a touch screen a focused text field means a keyboard is on its way:
  // the hero leaves at focus so the page is already compact when the
  // browser lays out for the keyboard. The CTA waits for the keyboard itself.
  const [touch] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches,
  );
  const compact = keyboard.open || (typing && touch);
  const seat = () => {
    const field = focusedField.current;
    if (field && scroller.current) seatField(field, scroller.current);
  };
  const onFieldFocus = (e: React.FocusEvent) => {
    if (!isTextField(e.target)) return;
    focusedField.current = e.target;
    setTyping(true);
    // Moving between fields under an open keyboard: seat the new one now. A
    // keyboard still rising is handled below, once the viewport reports it.
    if (keyboard.open) requestAnimationFrame(seat);
  };
  const onFieldBlur = (e: React.FocusEvent) => {
    if (focusedField.current !== e.target) return;
    focusedField.current = null;
    setTyping(false);
  };
  // A field removed with the focus still on it (a step change under an open
  // keyboard) fires no blur; typing must not outlive the field.
  useEffect(() => {
    if (focusedField.current && !focusedField.current.isConnected) {
      focusedField.current = null;
      setTyping(false);
    }
  }, [step]);
  // Seat once the viewport reports the keyboard (or a change in its height),
  // and again once its animation is over. Later viewport moves are the user's.
  useEffect(() => {
    if (!rearranging) return;
    const frame = requestAnimationFrame(seat);
    const settled = window.setTimeout(seat, 320);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settled);
      if (scroller.current) unseat(scroller.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rearranging, keyboard.height]);

  // The sheet tucks under each step's hero.
  const sheet = (content: React.ReactNode) => (
    <div
      key={step}
      className="pm-step-in pm-kb-sheet relative z-10 -mt-7 mx-auto flex w-full max-w-md flex-1 flex-col rounded-t-[28px] bg-background px-6 pb-[max(1rem,env(safe-area-inset-bottom))] pt-6"
    >
      {content}
    </div>
  );

  return (
    <KeyboardLayoutProvider value={{ open: keyboard.open, typing }}>
    <div
      ref={scroller}
      className="relative flex h-full w-full flex-col overflow-y-auto bg-[hsl(var(--brand-purple))]"
      data-keyboard={compact ? "open" : undefined}
      data-typing={rearranging ? "" : undefined}
      onFocus={onFieldFocus}
      onBlur={onFieldBlur}
    >
      <div className="pm-kb-steps z-10 px-6 pb-1 pt-4">
        <div
          className="mx-auto flex w-full max-w-md gap-1.5"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={position}
          aria-valuetext={`Step ${position} of ${total}: ${stepLabels(total)[position - 1]}`}
          aria-label={`Step ${position} of ${total}`}
        >
          {stepLabels(total).map((label, i) => (
            <div key={label} className="flex flex-1 flex-col gap-1.5">
              <div
                className={cn(
                  "h-1.5 rounded-full transition-colors duration-500",
                  i < position ? "bg-[hsl(var(--teal-bright))]" : "bg-white/25",
                )}
              />
              <span
                className={cn(
                  "pm-kb-hide text-[11px] font-semibold uppercase tracking-[0.08em]",
                  i < position ? "text-white" : "text-white/50",
                )}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {step === "who" && (
        <div key="who" className="pm-step-in z-10 mx-auto flex w-full max-w-md flex-1 flex-col">
          {/* `initial`/`greetingName` are read once at mount by design: a
              prefill arriving on a later host update() must not overwrite
              the user's in-progress edits. */}
          <WhoStep
            initial={draft || prefillInitial(prefill)}
            greetingName={
              draft
                ? `${draft.firstName} ${draft.lastName}`
                : prefill && prefill.firstName && prefill.lastName
                  ? `${prefill.firstName} ${prefill.lastName}`
                  : undefined
            }
            facilityName={currentFacility ? currentFacility.name : undefined}
            facilityState={currentFacility ? currentFacility.state : undefined}
            onCommit={commitWho}
          />
        </div>
      )}

      {step === "where" && draft && (
        <div className="pm-kb-hide relative z-10 mx-auto flex w-full max-w-md justify-center pb-0 pt-1">
          <img src={heroIllustration} alt="" className="h-[clamp(5rem,14vh,7.5rem)] w-auto" aria-hidden="true" />
        </div>
      )}

      {step === "where" &&
        draft &&
        sheet(
          <>
            {/* Sheet header: the way back on the left, live search state on
                the right. Out of the way while the facility search has the
                keyboard, like the rest of the question. */}
            {!rearranging && (
            <div className="mb-4 flex items-center justify-between gap-3">
              <button
                type="button"
                className="-ml-2 flex min-h-[44px] items-center gap-1 pr-2 text-[15px] font-semibold text-primary"
                onClick={backToWho}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back
              </button>
            <div
              className="inline-flex max-w-[60%] items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary"
              role="status"
              aria-live="polite"
            >
              {/* A failed create is disclosed once, on the wait surface, after
                  the minimum confidence wait — this chip keeps the searching
                  story so the flow never contradicts itself. */}
              <span
                className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary/30 border-t-primary motion-reduce:animate-none"
                aria-hidden="true"
              />
              <span className="truncate">{`Searching for ${draft.firstName}…`}</span>
            </div>
            </div>
            )}
            <WhereStep
              firstName={draft.firstName}
              state={draft.state || ""}
              initialFacility={knownFacility || facilityForDraft}
              query={facilityQuery}
              onQueryChange={setFacilityQuery}
              onMissChange={setFacilityMiss}
              onChangeState={backToWho}
              onKnownFacility={(facility) => {
                setWhereAnswered(true);
                setKnownFacility(facility);
                // The text found its facility: it is not a hint.
                setFacilityQuery("");
                setFacilityMiss(false);
                advanceTo("wait");
              }}
              onUnknown={() => {
                // Backing up and choosing this path drops the facility chosen before.
                setWhereAnswered(true);
                setKnownFacility(null);
                advanceTo("wait");
              }}
            />
          </>,
        )}

      {step === "wait" && draft && (
        <div className="relative z-0 mx-auto flex w-full max-w-md justify-center pb-4 pt-2">
          <PlaneOrbit active={!heroRest} className="h-[clamp(5.5rem,15vh,8rem)] w-auto" />
        </div>
      )}

      {step === "wait" &&
        draft &&
        sheet(
          <>
            <div className="mb-2 flex items-center">
              <button
                type="button"
                className="-ml-2 flex min-h-[44px] items-center gap-1 pr-2 text-[15px] font-semibold text-primary"
                onClick={goBack}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back
              </button>
            </div>
            <WaitStep
              key={waitGen}
              memory={waitMemory.current}
              onHeroRestChange={setHeroRest}
              searchId={searchId}
              draft={draft}
              knownFacility={knownFacility}
              searchFailed={searchFailed}
              addPending={add.pending}
              confirmFailed={add.failed === "confirm"}
              keepFailed={add.failed === "keep"}
              onEvent={emit}
              onConfirmMatch={(candidate, source) => void performAdd(candidate, source)}
              onKeepAdding={() => void performAdd(null)}
              onRetrySearch={retrySearch}
              lateBirthdate={lateBirthdate}
              onLateBirthdateChange={setLateBirthdate}
              onEscape={() => {
                if (onNavigate) onNavigate({ to: "my-penmates" });
              }}
            />
          </>,
        )}

      {step === "done" && draft && (
        <div className="pm-kb-hide relative z-10 mx-auto flex w-full max-w-md justify-center pb-0 pt-1">
          <img src={heroIllustration} alt="" className="h-[clamp(5rem,14vh,7.5rem)] w-auto" aria-hidden="true" />
        </div>
      )}

      {step === "done" &&
        draft &&
        sheet(
          <HandoffStep
            firstName={(added && added.first_name && callName(added.first_name)) || draft.firstName}
            onCompose={() => {
              // Land on the contact card, not straight in the composer: the card
              // is where the user chooses E-Message vs Letter & Photos (the legacy
              // decision point). Dropping into the composer skipped that choice
              // and opened every first message in letter mode.
              if (onNavigate) onNavigate({ to: "my-penmates" });
            }}
            onDone={() => {
              if (onNavigate) onNavigate({ to: "my-penmates" });
            }}
          />,
        )}
    </div>
    </KeyboardLayoutProvider>
  );
};
